import {Box, Grid, Typography, Button, CircularProgress} from "@mui/material";
import {useParams, useNavigate} from "react-router-dom";
import { useQueryCoordinate } from "../../clients/graphql/graphql.hooks";
import {styled, useTheme} from "@mui/material/styles";
import PortfolioCrudPanel from "../../views/portfolios/PortfolioCrudPanel";
import UserHistoricalValueChart from "../../components/tables/UserHistoricalValueChart";
import {PortfolioPositionsTable} from "../../components/tables/PortfolioPositionsTable";
import {InstitutionAuthLogsTable} from "../../components/tables/InstitutionAuthLogsTable";
import {FailedAutopilotOrdersTable} from "../../components/tables/FailedAutopilotOrdersTable";
import {whoisit} from "../../clients/graphql/pages/users-queries";
import {LegacyAdminButton} from "../../components/buttons/LegacyAdminButton";
import {AccountBalance} from "@mui/icons-material";
import {useUserPortfolioLpoaFetch} from "../../clients/graphql/pages/lpoa-queries";
import React from "react";
import {LpoaLoader} from "../../components/loaders/LpoaLoader";
import CalculateIcon from "@mui/icons-material/Calculate";
import {useCalculatePortfolioHistorySequences} from "./calculatePositionHistorySequencesMutations";
import {useState} from "react";
import {CalculationCompleteModal} from "../../components/CalculationCompleteModal";
import { SherlockInvestigateButton, SherlockWorkflowDrawer, balancingFractionalRemoverStepRenderers } from "../../components/sherlock";
interface ItemProps {
    bc: string;
}

const Item = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'bc',
})<ItemProps>(({ bc }) => ({
    border: `1px solid ${bc}`,
    borderRadius: '5px',
    marginTop: 10,
}));

export default function UserPortfolioPage() {
    const { portfolioKey } = useParams();
    const intPortfolioKey = parseInt(portfolioKey as string);
    const theme = useTheme();
    const navigate = useNavigate();
    const { data, loading, error } = useQueryCoordinate(whoisit, { variables: { portfolioKey: intPortfolioKey }, errorPolicy: "all" });
    const [calculationCompleteModalOpen, setCalculationCompleteModalOpen] = useState(false);
    const [sherlockDrawerOpen, setSherlockDrawerOpen] = useState(false);
    const [workflowExecutionId, setWorkflowExecutionId] = useState<string | undefined>(undefined);


    // LPOA data fetching
    const userKey = data?.whoisit?.user?.userKey;
    const {data: lpoaData} = useUserPortfolioLpoaFetch({ 
        userKey: userKey || 0 
    });

    console.log("UserKey", userKey)
    console.log("Portfolio Key", portfolioKey)
    const calculatePortfolioHistorySequencesAPI = useCalculatePortfolioHistorySequences(userKey, portfolioKey)

    if (loading) {
        return <div>Loading...</div>;
    }
    if (error && !data) {
        return <div>Error fetching data. Please reload page</div>;
    }

    const parsedUserKey = parseInt(userKey as string);
    const institutionKey = parseInt(data?.whoisit?.institution?.institutionKey as string);
    // const timeseriesPortfolios = data?.whoisit?.portfolio?.timeseriesPortfolios;
    const borderColor = theme.palette.mode === 'dark' ? '#666' : '#ccc'

    return(
      <>
        <Box>
            <Typography variant="h4" paddingBottom={2}>
                Portfolio&nbsp;
                <span style={{color: 'grey'}}>
                    #{portfolioKey}
                </span>
            </Typography>
            
            {/* LPOA Loader */}
            <LpoaLoader 
                lpoaData={lpoaData?.UserPortfolioLpoaFetch} 
                portfolioKey={intPortfolioKey}
            />
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <LegacyAdminButton path={`/portfolios/${portfolioKey}`} />
                <Button
                    variant="contained"
                    startIcon={<AccountBalance />}
                    onClick={() => navigate(`/portfolios/${portfolioKey}/positions/${userKey}`)}
                    sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#115293' } }}
                >
                    View Detailed Positions
                </Button>
              {userKey && portfolioKey &&
              <Button
                variant="contained"
                startIcon={<CalculateIcon/>}
                onClick={() => {
                  calculatePortfolioHistorySequencesAPI.mutate().then(() => {
                    setCalculationCompleteModalOpen(true)
                  })
                }}
                sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#115293' } }}
              >
                Recalculate Portfolio Positions
                {(calculatePortfolioHistorySequencesAPI.loading) && <>
                  <Box sx={{paddingLeft: "5px", position: "relative", top:".2rem"}}>
                    <CircularProgress size={20} sx={{color: "white"}}/>
                  </Box>
                </>
                }
              </Button>
              }
              <SherlockInvestigateButton 
                onClick={() => setSherlockDrawerOpen(true)}
              />
            </Box>
            {/*TimeseriesButton timeseriesPortfolios={timeseriesPortfolios} />*/}
            <Grid container spacing={2}>
                <Grid item xs={2.5}>
                    <Item bc={borderColor}>
                        <PortfolioCrudPanel 
                            portfolioKey={intPortfolioKey} 
                            userKey={userKey} 
                            deletedAt={data?.whoisit?.user?.deletedAt}
                            lpoaData={lpoaData?.UserPortfolioLpoaFetch}
                        />
                    </Item>
                </Grid>
                <Grid item xs={4.5}>
                    <Item sx={{ height: '33rem' }} bc={borderColor}>
                        <UserHistoricalValueChart portfolioKey={intPortfolioKey}/>
                    </Item>
                </Grid>
                <Grid item xs={5}>
                    <Item sx={{ height: '33rem' }} bc={borderColor}>
                        <InstitutionAuthLogsTable institutionKey={institutionKey} />
                    </Item>
                </Grid>
                <Grid item xs={7}>
                    <Item bc={borderColor}>
                        <PortfolioPositionsTable userKey={parsedUserKey} portfolioKey={intPortfolioKey} />
                    </Item>
                </Grid>
                <Grid item xs={5}>
                    <Item sx={{paddingLeft: '1rem', paddingRight: '1rem'}} bc={borderColor}>
                        <FailedAutopilotOrdersTable userKey={parsedUserKey} portfolioKey={intPortfolioKey} sx={{marginTop: '5rem', marginBottom: '1rem', paddingTop: '.5rem', marginLeft: '1rem'}} />
                    </Item>
                </Grid>
            </Grid>
        </Box>
        <CalculationCompleteModal open={calculationCompleteModalOpen} onClose={() => setCalculationCompleteModalOpen(false)}/>
        
        {/* Sherlock Workflow Drawer */}
        <SherlockWorkflowDrawer
          open={sherlockDrawerOpen}
          onClose={() => {
            setSherlockDrawerOpen(false);
            setWorkflowExecutionId(undefined);
          }}
          portfolioKey={intPortfolioKey}
          workflowExecutionId={workflowExecutionId}
          stepRenderers={balancingFractionalRemoverStepRenderers}
          onWorkflowStarted={(executionId) => setWorkflowExecutionId(executionId)}
        />
      </>
    )
}